import { PartDetailsDto, PartDto, SessionDto } from '../models/device.model';
import { PartSubType } from '../models/maintenancelogs.model';

function toNonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmedValue = value.trim();
  return trimmedValue.length > 0 ? trimmedValue : undefined;
}

export function normalizeDroneConfiguration(
  configuration: string | undefined,
): string {
  const value = toNonEmptyString(configuration)?.toLowerCase() ?? '';
  if (!value || value === 'unspecified') {
    return '';
  }

  if (value.includes('vector')) {
    return value.includes('r25')
      ? PartSubType.VECOTORR25
      : PartSubType.VECOTORR24;
  }

  if (value.includes('scorpion') || value.includes('scorpio')) {
    return value.includes('r25')
      ? PartSubType.SCORPIONR25
      : PartSubType.SCORPIONR24;
  }

  return configuration ?? '';
}

export function extractUavPartType(details?: PartDetailsDto): string {
  if (!details || details.type !== 'QsUavPart') {
    return '';
  }

  return toNonEmptyString(details.partType) ?? '';
}

export function extractUavPartSubType(details?: PartDetailsDto): string {
  if (!details || details.type !== 'QsUavPart') {
    return '';
  }

  return toNonEmptyString(details.partSubType) ?? '';
}

export function resolvePartConfiguration(part: PartDto): string {
  const partSubTypeConfiguration = normalizeDroneConfiguration(
    extractUavPartSubType(part.details),
  );
  if (partSubTypeConfiguration) {
    return partSubTypeConfiguration;
  }

  const detailsConfiguration = toNonEmptyString(
    part.details && part.details.type === 'QsUavPart'
      ? part.details.configuration
      : undefined,
  );

  return normalizeDroneConfiguration(detailsConfiguration);
}

export function resolveSessionConfiguration(session: SessionDto): string {
  const sessionDetailsConfiguration = normalizeDroneConfiguration(
    session.details?.configuration,
  );
  if (sessionDetailsConfiguration) {
    return sessionDetailsConfiguration;
  }

  const partConfiguration = (session.parts ?? [])
    .map((part) => resolvePartConfiguration(part))
    .find((configuration) => configuration.length > 0);

  return partConfiguration ?? '';
}

export function matchesDroneConfiguration(
  configuration: string,
  expectedConfigurations: string[],
): boolean {
  const normalizedConfiguration = normalizeDroneConfiguration(configuration);
  if (!normalizedConfiguration) {
    return false;
  }

  return expectedConfigurations.some((expectedConfiguration) => {
    const normalizedExpected = normalizeDroneConfiguration(
      expectedConfiguration,
    );
    if (!normalizedExpected) {
      return false;
    }

    if (normalizedExpected.includes('vector')) {
      return normalizedConfiguration.includes('vector');
    }

    if (normalizedExpected.includes('scorpion')) {
      return normalizedConfiguration.includes('scorpion');
    }

    return normalizedConfiguration === normalizedExpected;
  });
}
